import { ITEMS_PER_PAGE } from "#constants";
import { useCustomAttributeFilters } from "#hooks/FilterPanel/useCustomAttributeFilters";
import { useListEquipment } from "#hooks/adapters/useEquipment";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import { linkToEquipmentDetail, linkToFacilities } from "#routers/links";
import { EquipmentTableFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import useLocalization from "#src/hooks/useLocalization";
import {
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
} from "#src/utils/table";
import { useQueries } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  ItemAction,
  Pill,
  StorageKeys,
  useFilters,
} from "@validereinc/common-components";
import {
  AssetType,
  EquipmentAdapter,
  EquipmentAdapterQueryKeys,
  EquipmentType,
  SortDirection,
  type GroupedByEntryType,
} from "@validereinc/domain";
import { toFlattenedObject, toTitleCaseString } from "@validereinc/utilities";
import React, { useMemo } from "react";

export const EquipmentTablePanel = ({
  selected,
  onSelectionChange,
  isWithCustomAttributes = false,
  pageSize = ITEMS_PER_PAGE,
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  groupByTablesStateStorageKey,
}: {
  selected?: Record<string, EquipmentType>;
  onSelectionChange?: (newSelection: Record<string, EquipmentType>) => void;
  isWithCustomAttributes?: boolean;
  pageSize?: number;
} & Pick<
  StorageKeys,
  | "filterConfigStorageKey"
  | "tableConfigStorageKey"
  | "tableStateStorageKey"
  | "groupByTablesStateStorageKey"
>) => {
  const { localize, isLoading: isMappingLoading } = useLocalization();
  const [filters] = useFilters(filterConfigStorageKey);
  const { customAttributeHeaders, isLoading: isCustomAttributesLoading } =
    useCustomAttributeFilters({
      assetType: AssetType.EQUIPMENT,
    });

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
      pageSize,
    },
    filters,
    tableStateStorageKey
  );

  const equipmentListQueryPayload: Parameters<
    typeof EquipmentAdapter.getList
  >[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: toFlattenedObject(filters),
  };

  const {
    data,
    isLoading: isEquipmentLoading,
    isFetching: isEquipmentFetching,
  } = useListEquipment(equipmentListQueryPayload);
  const items = data?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...equipmentListQueryPayload,
        page: 1,
        pageSize: 10,
        sortBy: tableState.sortBy,
        sortDirection: tableState.sortDirection,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map(
      (key) => groupByTablesStates.get(key) ?? equipmentListQueryPayload
    )
  );

  const equipmentGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: EquipmentAdapterQueryKeys.getList(d.payload),
      queryFn: () => EquipmentAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? equipmentGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const headers: Array<HeaderType<EquipmentType>> = useMemo(
    () => [
      {
        label: `${localize("Equipment")} Name`,
        key: "name",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) =>
          item?.id && item?.name ? (
            <RoutingLink to={linkToEquipmentDetail(item.id)}>
              {item.name}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      {
        label: `${localize("Equipment")} Type`,
        key: "type_id",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => item?.type?.name ?? "-",
      },
      {
        label: `${localize("Facility")} Name`,
        key: "facility_name",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) =>
          item?.facility_id && item?.facility?.name ? (
            <RoutingLink to={linkToFacilities(item.facility_id)}>
              {item.facility.name}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      {
        key: "status",
        label: "Status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill variant={item.status === "active" ? "success" : "default"}>
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      {
        label: "Effective Date",
        key: "effective_date",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell value={item.effective_date} />
        ),
      },
      ...(isWithCustomAttributes ? customAttributeHeaders : []),
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [
      localize,
      isWithCustomAttributes,
      customAttributeHeaders,
      metaHeaders,
      metaExtendedHeaders,
    ]
  );

  const getItemActions = ({ item }: { item: EquipmentType }): ItemAction[] =>
    selected?.[item.id]
      ? [
          {
            label: "Remove",
            buttonProps: {
              onClick: () => {
                const { [item.id]: _, ...newSelection } = selected;
                onSelectionChange?.(newSelection);
              },
              icon: "trash",
            },
          },
        ]
      : [
          {
            label: "Add",
            buttonProps: {
              onClick: () =>
                onSelectionChange?.({
                  ...selected,
                  [item.id]: item,
                }),
              icon: "plus-circle",
            },
          },
        ];

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: equipmentGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
    getItemActions,
  });

  const loading =
    isMappingLoading || isEquipmentLoading || isCustomAttributesLoading;

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{ title: localize("Equipment"), isFluidY: false }}
      dataTableProps={{
        intent: "simplicity-first",
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        isLoading: loading,
        isBusy: isEquipmentFetching,
        headers,
        items,
        getItemId: (item) => item.id,
        getItemActions,
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: data?.total_entries,
        },
        onPaginationChange: updateTableState,
        onSortChange: updateTableState,
        selected,
        onSelectionChange,
      }}
      filterComponent={
        <EquipmentTableFilterArea
          filterConfigStorageKey={filterConfigStorageKey}
          hasCustomAttributeFilters={isWithCustomAttributes}
        />
      }
      {...groupByDataTableProps}
    />
  );
};
